import { useState, useEffect } from 'react';
import { BookOpen, Edit, Save, X, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../services/api';
import { getModules, getSubModules, getModuleCourses } from '../../mockApi';
import type { Module, SubModule, ModuleCourse, CourseContent } from '../../types';

interface AdminCourseData {
  id: string;
  title: string;
  description: string;
  hasVideo: boolean;
  icon?: string;
  free?: boolean;
  module_code: string;
  module_title: string;
  sub_module_title: string;
  sub_module_id: string;
  content?: string; // Loaded on demand
  video_url?: string; // Loaded on demand for video courses
}

export default function AdminCourses() {
  const { theme } = useTheme();
  const [courses, setCourses] = useState<AdminCourseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editVideoUrl, setEditVideoUrl] = useState('');
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [loadingContent, setLoadingContent] = useState<Record<string, boolean>>({});
  const [viewingCourse, setViewingCourse] = useState<string | null>(null);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    setLoading(true);
    try {
      // Récupérer tous les modules
      const modules = await getModules();
      const allCourses: AdminCourseData[] = [];

      // Pour chaque module, récupérer les sous-modules et leurs cours
      for (const module of modules) {
        const subModules = await getSubModules(module.id);

        for (const subModule of subModules) {
          const moduleCourses = await getModuleCourses(subModule.id);

          // Pour chaque cours, créer l'objet sans le contenu
          for (const course of moduleCourses) {
            allCourses.push({
              id: course.id,
              title: course.title,
              description: course.description,
              hasVideo: course.has_video,
              icon: course.icon,
              free: course.free,
              module_code: module.code,
              module_title: module.title,
              sub_module_title: subModule.title,
              sub_module_id: subModule.id
            });
          }
        }
      }

      setCourses(allCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCourseContent = async (courseId: string) => {
    if (loadingContent[courseId]) return;

    setLoadingContent(prev => ({ ...prev, [courseId]: true }));

    try {
      const { getCourseContent } = await import('../../mockApi');
      const courseContent = await getCourseContent(courseId);

      setCourses(courses.map(course =>
        course.id === courseId
          ? { ...course, content: courseContent.content, video_url: courseContent.video_url }
          : course
      ));
    } catch (error) {
      console.error(`Error loading content for course ${courseId}:`, error);
    } finally {
      setLoadingContent(prev => ({ ...prev, [courseId]: false }));
    }
  };

  const handleViewCourse = async (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    if (!course?.content) {
      await loadCourseContent(courseId);
    }
    setViewingCourse(courseId);
  };

  const handleEditCourse = async (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    if (!course?.content) {
      await loadCourseContent(courseId);
    }
    const updatedCourse = courses.find(c => c.id === courseId);
    setEditingCourse(courseId);
    setEditContent(updatedCourse?.content || '');
    setEditVideoUrl(updatedCourse?.video_url || '');
    setViewingCourse(null);
  };

  const handleSaveCourse = async (courseId: string) => {
    try {
      await api.updateCourseContent(courseId, {
  content: editContent,
  video_url: editVideoUrl
});
      setCourses(courses.map(course =>
        course.id === courseId
          ? { ...course, content: editContent, video_url: editVideoUrl }
          : course
      ));
      setEditingCourse(null);
      setEditContent('');
      setEditVideoUrl('');
    } catch (error) {
      console.error('Error updating course:', error);
      alert('Erreur lors de la mise à jour du cours');
    }
  };

  const toggleModule = (moduleCode: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleCode]: !prev[moduleCode]
    }));
  };

  const groupCoursesByModule = (courses: AdminCourse[]) => {
    const grouped = courses.reduce((acc, course) => {
      if (!acc[course.module_code]) {
        acc[course.module_code] = {
          title: course.module_title,
          courses: []
        };
      }
      acc[course.module_code].courses.push(course);
      return acc;
    }, {} as Record<string, { title: string; courses: AdminCourseData[] }>);

    const sortedModules = Object.keys(grouped).sort((a, b) => {
      const getOrder = (code: string) => {
        if (code.startsWith('BC')) {
          const num = parseInt(code.replace('BC', ''));
          return num;
        }
        return 999;
      };
      return getOrder(a) - getOrder(b);
    });

    return sortedModules.map(code => ({
      code,
      title: grouped[code].title,
      courses: grouped[code].courses
    }));
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className={`w-8 h-8 border-4 border-${theme.primary} border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
        <p className="text-gray-600">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Gestion des cours ({courses.length})</h2>
      {courses.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Aucun cours trouvé</p>
      ) : (
        groupCoursesByModule(courses).map((module) => (
          <div key={module.code} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleModule(module.code)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <span className={`px-3 py-1 bg-${theme.secondary} text-${theme.text} text-sm font-medium rounded mr-3`}>
                  {module.code}
                </span>
                <h3 className="font-semibold text-left">{module.title}</h3>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">
                  {module.courses.length} cours
                </span>
                {expandedModules[module.code] ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {expandedModules[module.code] && (
              <div className="border-t border-gray-100">
                {module.courses.map((course) => (
                  <div key={course.id} className="px-6 py-4 border-b last:border-b-0 border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="font-medium mb-1">{course.title}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className={`px-2 py-1 rounded text-xs ${
                            course.content ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {course.content ? 'Contenu chargé' : 'Contenu non chargé'}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {!editingCourse && (
                          <button
                            onClick={() => handleViewCourse(course.id)}
                            disabled={loadingContent[course.id]}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                            title="Voir le contenu"
                          >
                            {loadingContent[course.id] ? (
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {course.content && editingCourse === course.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSaveCourse(course.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="Sauvegarder"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingCourse(null);
                                setEditContent('');
                                setViewingCourse(null);
                              }}
                              className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                              title="Annuler"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : course.content && !editingCourse ? (
                          <button
                            onClick={() => handleEditCourse(course.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Éditer"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {/* Content Display/Edit */}
                    {(editingCourse === course.id || viewingCourse === course.id) && (
                      <div className="mt-4">
                        {editingCourse === course.id ? (
                          <div>
                            {course.hasVideo && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Lien de la vidéo :
                                </label>
                                <input
                                  type="text"
                                  value={editVideoUrl}
                                  onChange={(e) => setEditVideoUrl(e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded"
                                  placeholder="https://player.vimeo.com/..."
                                />
                              </div>
                            )}
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Contenu Markdown:
                            </label>
                            <textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="w-full h-64 p-3 border border-gray-300 rounded-lg font-mono text-sm"
                              placeholder="Contenu du cours en Markdown..."
                            />
                          </div>
                        ) : viewingCourse === course.id && course.content ? (
                          <div className="space-y-4">
                            {course.hasVideo && course.video_url ? (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Lien de la vidéo:
                                </label>
                                <div className="bg-gray-50 p-3 rounded-lg border">
                                  <a
                                    href={course.video_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline break-all"
                                  >
                                    {course.video_url}
                                  </a>
                                </div>
                              </div>
                            ) : null}
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Contenu du cours:
                              </label>
                              <button
                                onClick={() => setViewingCourse(null)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Fermer"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto border">
                              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                                {course.content}
                              </pre>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}